import { ReactNode } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { formatToGermanNumber } from 'src/common/toGermanNumber';
import { PrognosisDetailsDto, RangeDto, SteckbriefCalculationResultDto } from '../ensan-steckbrief.dto';
import {
    CO2_SUPPRESSION_REASON_LABEL_KEYS,
    EVEBI_DEFAULT_NOTE_KEY,
    PROGNOSIS_BRANCH_LABEL_KEYS,
    affectedChips,
    assumptionMessage,
    formatAssumptionNumber,
} from '../steckbrief-calculation-messages';
import {
    BUILDING_GEOMETRY_METRIC_HELP_TEXT_KEY,
    EnergyPriceFieldKey,
    variantFieldByKey,
} from '../steckbrief-variant-fields';
import { SteckbriefInvestmentBreakdown } from './SteckbriefInvestmentBreakdown';

interface SteckbriefAssumptionsSectionProps {
    /** The latest `/calculate` result; null before the first run completed. */
    calc: SteckbriefCalculationResultDto | null;
    /** True while a `/calculate` run is in flight — the section marks itself as updating. */
    loading: boolean;
    /**
     * Which tariffs that run had ENTERED (GEN-788) — the others were the EVEBI
     * defaults. Absent reads as "none entered".
     */
    enteredEnergyPrices?: Record<EnergyPriceFieldKey, boolean>;
    /** "Refresh PV estimate" of the investment breakdown (GEN-799); absent hides the action. */
    onRefreshPvEstimate?: () => void;
    /** True while that refresh request is in flight. */
    pvEstimateRefreshing?: boolean;
    /**
     * The form values that run was computed from (GEN-798 follow-up): the
     * display-only building surfaces (footprint, raw / real wall surface,
     * neighbour count) are read from here, the derived window and facade areas
     * from `calc`. Absent/null hides the block.
     */
    inputValues?: Record<string, unknown> | null;
}

const NO_ENTERED_ENERGY_PRICES: Record<EnergyPriceFieldKey, boolean> = { heatPrice: false, electricityPrice: false };

/** German-style number ('–' for missing values); wraps the shared de-DE formatter. */
function fmt(value: number | null | undefined, digits = -1): string {
    if (value === null || value === undefined) return '–';
    return formatToGermanNumber(value, digits);
}

/** German-style "number unit" — a missing value renders a PLAIN dash, no unit. */
function fmtWithUnit(value: number | null | undefined, unit: string): string {
    if (value === null || value === undefined) return '–';
    return `${fmt(value)} ${unit}`;
}

/** German-style "low – high unit" range ('–' for a missing range). */
function fmtRange(range: RangeDto | null | undefined, unit: string): string {
    if (!range) return '–';
    return `${fmt(range.low)} – ${fmt(range.high)} ${unit}`;
}

/** One label/value row of the "Abgeleitete Werte" block. */
function DerivedValueRow({ label, value, testId }: { label: string; value: ReactNode; testId: string }) {
    return (
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }} data-testid={testId}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
                {value}
            </Typography>
        </Stack>
    );
}

/** A finite number from a form value, else null (display-only surfaces). */
function numberOf(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * "Building surfaces" (GEN-798 follow-up): the surface figures behind the
 * investment estimate, in derivation order, NOT editable — the CityGML
 * footprint and wall surfaces (prefilled via the Data Catalog), the window area
 * (real × share) and the opaque facade (real − window) as this run derived them,
 * and the neighbour count. The labels are the form-field labels of the keys.
 */
function BuildingSurfacesBlock({
    calc,
    inputValues,
}: {
    calc: SteckbriefCalculationResultDto;
    inputValues: Record<string, unknown>;
}) {
    const { t } = useTranslation();
    const label = (key: string) => t(variantFieldByKey(key)?.labelKey ?? key);
    const raw = numberOf(inputValues['rawSurfaceArea']);
    const real = numberOf(inputValues['realSurfaceArea']);
    const share = numberOf(inputValues['windowAreaSharePercent']);
    const windowArea = calc.windowSurfaceArea ?? null;
    const facade = calc.facadeSurfaceArea ?? null;
    const partyWalls = raw !== null && real !== null && raw > real ? raw - real : null;
    const adjacent = numberOf(inputValues['adjacentBuildingCount']);
    return (
        <Box data-testid="building-surfaces">
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {t('Building surfaces')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 0.5,
                }}
            >
                <DerivedValueRow
                    label={label('footprintArea')}
                    value={fmt(numberOf(inputValues['footprintArea']))}
                    testId="derived-footprint-area"
                />
                <DerivedValueRow label={label('rawSurfaceArea')} value={fmt(raw)} testId="derived-raw-surface-area" />
                <DerivedValueRow
                    label={label('realSurfaceArea')}
                    value={
                        partyWalls === null
                            ? fmt(real)
                            : `${fmt(real)} (${t('minus {{area}} m² party walls', { area: fmt(partyWalls) })})`
                    }
                    testId="derived-real-surface-area"
                />
                <DerivedValueRow
                    label={label('windowSurfaceArea')}
                    value={
                        windowArea === null || share === null
                            ? fmt(windowArea)
                            : `${fmt(windowArea)} (${t('{{share}} % of the real wall surface', { share: fmt(share) })})`
                    }
                    testId="derived-window-surface-area"
                />
                <DerivedValueRow
                    label={label('facadeSurfaceArea')}
                    value={facade === null ? fmt(facade) : `${fmt(facade)} (${t('real wall surface − windows')})`}
                    testId="derived-facade-surface-area"
                />
                <DerivedValueRow
                    label={label('adjacentBuildingCount')}
                    value={fmt(adjacent)}
                    testId="derived-adjacent-building-count"
                />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {t(BUILDING_GEOMETRY_METRIC_HELP_TEXT_KEY)}
            </Typography>
        </Box>
    );
}

/** A code's translated label, the raw code for an unknown one, '–' for null. */
function codeLabel(t: TFunction, labelKeys: Record<string, string>, code: string | null): string {
    if (code === null) return '–';
    const labelKey = labelKeys[code];
    return labelKey === undefined ? code : t(labelKey);
}

/** A tariff in €/kWh with its own precision, never below two decimals ("0,41", "0,1075"). */
function fmtPrice(value: number): string {
    return `${formatAssumptionNumber('de', value, 2)} €/kWh`;
}

/**
 * "Prognosemodell" (GEN-788): the derivation behind the energy/CO₂/cost
 * prognosis of the calibrated sheet — which rule applied, the envelope score and
 * its components, the demand estimate the CO₂, the energy costs (and the
 * district rule) stand on, the use ratio, the CO₂ baseline, the tariffs used
 * (entered or the EVEBI default), the energy costs and their implied price, and
 * the independently computed point values. Every number is the backend's,
 * rounded at the response boundary.
 */
function PrognosisModelBlock({
    prognosis,
    enteredEnergyPrices,
}: {
    prognosis: PrognosisDetailsDto;
    enteredEnergyPrices: Record<EnergyPriceFieldKey, boolean>;
}) {
    const { t } = useTranslation();
    /**
     * "0,12 €/kWh (EVEBI default)" / "0,15 €/kWh (entered)" — '–' when the run
     * had no tariff, or the backend predates the sheet v6 cost fields (absent).
     */
    const tariff = (value: number | null | undefined, entered: boolean) =>
        value === null || value === undefined
            ? '–'
            : `${fmtPrice(value)} (${t(entered ? 'entered' : EVEBI_DEFAULT_NOTE_KEY)})`;
    /** Absent (not null) on a backend before the sheet v6 cost fields, like the tariffs. */
    const impliedPrice: number | null | undefined = prognosis.impliedPriceEurPerKwh;
    /** The tariff rows are labelled like the form fields they come from. */
    const fieldLabel = (key: EnergyPriceFieldKey) => t(variantFieldByKey(key)?.labelKey ?? key);
    const components = [
        { labelKey: 'Roof', score: prognosis.roofScore },
        { labelKey: 'Facade', score: prognosis.facadeScore },
        { labelKey: 'Basement ceiling', score: prognosis.basementCeilingScore },
        { labelKey: 'Windows', score: prognosis.windowScore },
    ];
    const envelope =
        prognosis.envelopeScore === null
            ? '–'
            : `${fmt(prognosis.envelopeScore)} (${components
                  .map(({ labelKey, score }) => `${t(labelKey)} ${fmt(score)}`)
                  .join(' · ')})`;
    const demand =
        prognosis.energyDemandEstimateKwh === null
            ? '–'
            : `${fmt(prognosis.energyDemandEstimateKwh)} kWh/a (${t('heat')} ${fmt(prognosis.heatDemandEstimateKwh)} · ${t(
                  'building electricity',
              )} ${fmt(prognosis.buildingElectricityEstimateKwh)})`;
    const co2Baseline =
        prognosis.co2BaselineTons === null
            ? '–'
            : `${fmt(prognosis.co2BaselineTons)} t/a${
                  prognosis.heatEmissionFactor === null
                      ? ''
                      : ` (f_heat ${fmt(prognosis.heatEmissionFactor)} kg CO₂e/kWh)`
              }`;
    return (
        <Box data-testid="prognosis-model">
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {t('Prediction model')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 0.5,
                }}
            >
                <DerivedValueRow
                    label={t('Prediction rule')}
                    value={codeLabel(t, PROGNOSIS_BRANCH_LABEL_KEYS, prognosis.branch)}
                    testId="derived-prognosis-rule"
                />
                <DerivedValueRow
                    label={t('Switch to heat pump')}
                    value={prognosis.heatPumpSwitch ? t('yes') : t('no')}
                    testId="derived-prognosis-heat-pump-switch"
                />
                <DerivedValueRow label={t('Envelope score')} value={envelope} testId="derived-envelope-score" />
                <DerivedValueRow label={t('Energy demand estimate')} value={demand} testId="derived-demand-estimate" />
                <DerivedValueRow
                    label={t('Estimated specific heat demand')}
                    value={fmtWithUnit(prognosis.specificHeatDemandEstimate, 'kWh/(m²a)')}
                    testId="derived-specific-heat-demand-estimate"
                />
                <DerivedValueRow
                    label={t('Use ratio (meters ÷ estimate)')}
                    value={fmt(prognosis.useRatio, 2)}
                    testId="derived-use-ratio"
                />
                <DerivedValueRow
                    label={t('CO₂ baseline (estimate)')}
                    value={co2Baseline}
                    testId="derived-co2-baseline"
                />
                <DerivedValueRow
                    label={t('Energy saving potential (point)')}
                    value={fmtWithUnit(prognosis.energySavingPotentialPoint, '%')}
                    testId="derived-energy-saving-point"
                />
                <DerivedValueRow
                    label={t('CO₂ saving (point)')}
                    value={fmtWithUnit(prognosis.co2SavingPoint, 't/a')}
                    testId="derived-co2-saving-point"
                />
                {prognosis.co2SuppressionReason !== null && (
                    <DerivedValueRow
                        label={t('CO₂ not predicted')}
                        value={codeLabel(t, CO2_SUPPRESSION_REASON_LABEL_KEYS, prognosis.co2SuppressionReason)}
                        testId="derived-co2-suppression-reason"
                    />
                )}
                {/* Sheet v6 H3: the tariffs, the energy costs they price, the cost saving. */}
                <DerivedValueRow
                    label={fieldLabel('heatPrice')}
                    value={tariff(prognosis.heatPriceEurPerKwh, enteredEnergyPrices.heatPrice)}
                    testId="derived-heat-price"
                />
                <DerivedValueRow
                    label={fieldLabel('electricityPrice')}
                    value={tariff(prognosis.electricityPriceEurPerKwh, enteredEnergyPrices.electricityPrice)}
                    testId="derived-electricity-price"
                />
                <DerivedValueRow
                    label={t('Energy costs baseline (estimate: demand × tariffs)')}
                    value={fmtWithUnit(prognosis.energyCostEstimateEur, '€/a')}
                    testId="derived-energy-cost-estimate"
                />
                <DerivedValueRow
                    label={t('Implied price (energy costs ÷ meters)')}
                    value={impliedPrice === null || impliedPrice === undefined ? '–' : `${fmt(impliedPrice, 3)} €/kWh`}
                    testId="derived-implied-price"
                />
                <DerivedValueRow
                    label={t('Cost saving share of the energy costs (point)')}
                    value={fmtWithUnit(prognosis.costSavingSharePercent, '%')}
                    testId="derived-cost-saving-share-point"
                />
                <DerivedValueRow
                    label={t('Cost saving (point)')}
                    value={fmtWithUnit(prognosis.costSavingPoint, '€/a')}
                    testId="derived-cost-saving-point"
                />
            </Box>
            {/* Notes B14/B20 of the sheet: the band is the product, the point is fitted separately. */}
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {t('Point values are computed independently and are NOT the midpoint of the band.')}
            </Typography>
        </Box>
    );
}

/**
 * Read-only ops-visibility section under the Steckbrief Variant form (GEN-756):
 * "Annahmen & abgeleitete Werte". Renders from the LATEST `/calculate` result
 * and live-updates with every run — nothing here is stamped or printed.
 *
 * Block 1 (Annahmen) lists the assumptions the engines ACTUALLY applied in that
 * run — each entry arrives as a stable code plus raw params (GEN-758: the API
 * is language-agnostic) and is translated client-side into the active UI
 * language with per-locale number formatting; an unknown code renders honestly
 * as code + params. The affected form field / measure is labeled when the
 * entry names one. Zero fired assumptions show an explicit "none" wording,
 * never a silently hidden block. Block 2 (Abgeleitete Werte) shows the derived
 * outputs the run computed, straight from the existing response fields, and —
 * since GEN-788 — the prognosis model's derivation (see [PrognosisModelBlock]).
 * Since the GEN-798 follow-up the non-editable building surfaces (footprint,
 * raw / real wall surface, window, facade, neighbours) show here too — see
 * [BuildingSurfacesBlock].
 * Block 3 (Investitionskosten, GEN-780) itemises the investment estimate per
 * selected measure — see [SteckbriefInvestmentBreakdown].
 */
export function SteckbriefAssumptionsSection({
    calc,
    loading,
    enteredEnergyPrices = NO_ENTERED_ENERGY_PRICES,
    onRefreshPvEstimate,
    pvEstimateRefreshing = false,
    inputValues = null,
}: SteckbriefAssumptionsSectionProps) {
    const { t, i18n } = useTranslation();

    // Defensive until the backend GEN-756 PR deploys: an absent field reads as
    // "no assumptions reported" (see the DTO comment).
    const assumptions = calc?.assumptions ?? [];

    return (
        <Box
            data-testid="assumptions-section"
            sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
        >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2">{t('Assumptions & derived values')}</Typography>
                <Chip size="small" variant="outlined" label={t('Read-only')} />
                {loading && <CircularProgress size={12} />}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                {t('Applied by the calculation engines in the last run — updates with every calculation, not printed.')}
            </Typography>

            {calc === null ? (
                // Pre-first-calculation state: nothing has been computed yet.
                <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="assumptions-pre-calc">
                    {t('The values appear after the first calculation.')}
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                            {t('Assumptions')}
                        </Typography>
                        {assumptions.length === 0 ? (
                            <Typography variant="body2" data-testid="assumptions-empty">
                                {t('No assumptions — all values from recorded data.')}
                            </Typography>
                        ) : (
                            <Stack spacing={0.75} data-testid="assumptions-list">
                                {assumptions.map((assumption, index) => (
                                    <Stack
                                        // Codes may legitimately fire more than once
                                        // (e.g. one renovation assumption per measure),
                                        // so the position joins the key.
                                        key={`${assumption.code}-${index}`}
                                        direction="row"
                                        spacing={1}
                                        useFlexGap
                                        sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
                                        data-testid={`assumption-${assumption.code}`}
                                    >
                                        {affectedChips(t, assumption).map(({ key, label }) => (
                                            <Chip key={key} size="small" variant="outlined" label={label} />
                                        ))}
                                        {/* Client-translated code+params message (GEN-758) — numbers
                                            formatted per the active UI language. */}
                                        <Typography variant="body2">
                                            {assumptionMessage(t, i18n.language, assumption)}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                            {t('Derived values')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                columnGap: 3,
                                rowGap: 0.5,
                            }}
                        >
                            <DerivedValueRow
                                label={t('Specific heat demand')}
                                value={fmtWithUnit(calc.specificHeatDemand, 'kWh/(m²a)')}
                                testId="derived-specific-heat-demand"
                            />
                            <DerivedValueRow
                                label={t('Specific electricity demand')}
                                value={fmtWithUnit(calc.specificElectricityDemand, 'kWh/(m²a)')}
                                testId="derived-specific-electricity-demand"
                            />
                            <DerivedValueRow
                                label={t('Orientation value (heat + electricity per NGF)')}
                                value={fmtWithUnit(calc.orientationValue, 'kWh/(m²a)')}
                                testId="derived-orientation-value"
                            />
                            <DerivedValueRow
                                label={t('Efficiency level (current / target)')}
                                value={`${calc.orientationClass ?? '–'} / ${calc.targetClass ?? '–'}`}
                                testId="derived-efficiency-levels"
                            />
                            <DerivedValueRow
                                label={t('Renovation need')}
                                value={calc.renovationNeedBadge ?? '–'}
                                testId="derived-renovation-need"
                            />
                            <DerivedValueRow
                                label={t('Energy saving potential')}
                                value={fmtRange(calc.energySavingPotentialRange, '%')}
                                testId="derived-energy-saving"
                            />
                            <DerivedValueRow
                                label={t('CO₂ saving')}
                                value={fmtRange(calc.co2SavingRange, 't/a')}
                                testId="derived-co2-saving"
                            />
                            <DerivedValueRow
                                label={t('Cost saving')}
                                value={fmtRange(calc.costSavingRange, '€/a')}
                                testId="derived-cost-saving"
                            />
                            <DerivedValueRow
                                label={t('Heat consumption')}
                                value={
                                    calc.heatConsumptionKwh == null
                                        ? '–'
                                        : `${fmt(calc.heatConsumptionKwh)} kWh/a${
                                              calc.heatConsumptionYear == null ? '' : ` (${calc.heatConsumptionYear})`
                                          }`
                                }
                                testId="derived-heat-consumption"
                            />
                            <DerivedValueRow
                                label={t('Electricity consumption')}
                                value={
                                    calc.electricityConsumptionKwh == null
                                        ? '–'
                                        : `${fmt(calc.electricityConsumptionKwh)} kWh/a${
                                              calc.electricityConsumptionYear == null
                                                  ? ''
                                                  : ` (${calc.electricityConsumptionYear})`
                                          }`
                                }
                                testId="derived-electricity-consumption"
                            />
                            {/* The GEN-780 hourly heat consumption has no row: it is
                                backend-only and shows as the heat pump's kW quantity
                                in the investment breakdown below. The window area moved
                                to the building-surfaces block (GEN-798 follow-up). */}
                        </Box>
                    </Box>

                    {/* GEN-798 follow-up: the non-editable building surfaces. */}
                    {inputValues && <BuildingSurfacesBlock calc={calc} inputValues={inputValues} />}

                    {/* GEN-788: the prognosis model's derivation — absent on older
                        backends, null when no prognosis ran (empty selection). */}
                    {calc.prognosis && (
                        <PrognosisModelBlock prognosis={calc.prognosis} enteredEnergyPrices={enteredEnergyPrices} />
                    )}

                    {/* GEN-780: the item-by-item investment estimate behind the
                        printed totals — ops visibility of quantities and rates. */}
                    <SteckbriefInvestmentBreakdown
                        calc={calc}
                        onRefreshPvEstimate={onRefreshPvEstimate}
                        pvEstimateRefreshing={pvEstimateRefreshing}
                    />
                </Stack>
            )}
        </Box>
    );
}
